import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import PageTitle from "../components/PageTitle";
import { PrimaryButton } from "../components/Button";
import LabeledInput from "../components/LabeledInput";
import SegmentedGroup from "../components/SegmentedGroup";
import AddressTypeBottomSheet from "../components/AddressTypeBottomSheet";
import { getUserAddresses, updateUserAddresses } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const AddAddress = () => {
  const [name, setName] = useState("");
  const [addressType, setAddressType] = useState("");
  const [flatDetails, setFlatDetails] = useState("");
  const [landmark, setLandmark] = useState("");
  const [showAddressTypeSheet, setShowAddressTypeSheet] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [addressData, setAddressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Handle edit mode initialization
  useEffect(() => {
    if (location.state?.editMode && location.state?.addressData) {
      setIsEditMode(true);
      setAddressData(location.state.addressData);
      // Pre-populate form with existing address data
      const addr = location.state.addressData;
      setName(addr.label || '');
      // Try to match address type from label, or use a default
      const labelLower = (addr.label || '').toLowerCase();
      if (labelLower.includes('home')) {
        setAddressType('home');
      } else if (labelLower.includes('gym')) {
        setAddressType('gym');
      } else if (labelLower.includes('park')) {
        setAddressType('park');
      } else {
        setAddressType('home'); // default
      }
      setFlatDetails(addr.street || '');
      setLandmark(addr.landmark || '');
    } else if (location.state?.addressData && !location.state?.editMode) {
      // If addressData exists but not in edit mode, still set it for reference
      setAddressData(location.state.addressData);
    }
  }, [location.state]);

  useEffect(() => {
    if (isEditMode) return;
    const locationDetails = location.state?.locationDetails;
    if (!locationDetails) return;

    if (!flatDetails && locationDetails.formattedAddress) {
      const parts = locationDetails.formattedAddress.split(',');
      setFlatDetails(parts[0]?.trim() || locationDetails.formattedAddress);
    }

    if (!name && locationDetails.formattedAddress) {
      setName(locationDetails.formattedAddress.trim());
    }
  }, [isEditMode, location.state, flatDetails, name]);

  const handleSaveAddress = async () => {
    if (!user?._id) {
      setError('User not authenticated');
      return;
    }

    if (!name.trim() || !addressType) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get current addresses
      const currentResponse = await getUserAddresses(user._id);
      const currentAddresses = currentResponse.addresses || [];

      // Create new address object
      const locationDetails = location.state?.locationDetails;

      // Extract street from formatted address if flatDetails is empty
      let streetValue = flatDetails;
      if (!streetValue && locationDetails?.formattedAddress) {
        const parts = locationDetails.formattedAddress.split(',');
        streetValue = parts[0] || '';
      }

      const newAddress = {
        street: streetValue || 'Address',
        city: locationDetails?.city || addressData?.city || 'City',
        state: locationDetails?.state || addressData?.state || 'State',
        postalCode: locationDetails?.postalCode || addressData?.postalCode || '',
        country: locationDetails?.country || addressData?.country || 'Country',
        label: name.trim(),
        landmark: landmark.trim() || '',
        // Store coordinates if available
        ...(locationDetails?.lat && locationDetails?.lng && {
          coordinates: {
            lat: locationDetails.lat,
            lng: locationDetails.lng
          }
        })
      };

      let updatedAddresses;
      if (isEditMode && addressData?._id) {
        // Update existing address - preserve the _id
        updatedAddresses = currentAddresses.map(addr => {
          // Convert both IDs to strings for comparison
          const addrIdStr = addr._id ? addr._id.toString() : '';
          const targetIdStr = addressData._id ? addressData._id.toString() : '';
          if (addrIdStr === targetIdStr) {
            return { ...addr, ...newAddress, _id: addressData._id }; // Preserve original _id
          }
          return addr;
        });
      } else {
        // Add new address
        updatedAddresses = [...currentAddresses, newAddress];
      }

      // Update addresses via API
      await updateUserAddresses({
        userId: user._id,
        addresses: updatedAddresses
      });

      // Navigate back to where user came from, or default to saved addresses
      const returnTo = location.state?.from || "/student/saved-addresses";
      navigate(returnTo);
    } catch (err) {
      console.error('Error saving address:', err);
      setError('Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressTypeSelect = (type) => {
    setAddressType(type);
  };

  return (
    <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>

      <div className="w-full min-h-full flex flex-col">
        {/* NAVBAR */}
        <Navbar
          onBack={() => {
            // Navigate back to set-location if in edit mode, otherwise go back
            if (isEditMode && location.state?.editMode) {
              navigate("/student/set-location", {
                state: {
                  editMode: true,
                  addressData: addressData,
                  from: location.state?.from || "/student/saved-addresses"
                }
              });
            } else {
              navigate(-1);
            }
          }}
          background="transparent"
          spacerHeight={40}
        />

        {/* CONTENT */}
        <main className="flex-1 bg-transparent px-5">
          <div className="pt-4">
            {/* TITLE */}
            <PageTitle>{isEditMode ? "Edit Address" : "Add Address"}</PageTitle>

            {/* ERROR MESSAGE */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* NAME OF ADDRESS */}
            <div className="mb-2">
              <LabeledInput
                label="Name of Address"
                placeholder="Name of Address"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* ADDRESS TYPE TAGS */}
            <div className="mb-6">
              <SegmentedGroup
                label=""
                options={[
                  { label: "Home", value: "home" },
                  { label: "Gym", value: "gym" },
                  { label: "Park", value: "park" }
                ]}
                value={addressType}
                onChange={(value) => setAddressType(value)}
                size="sm"
              />
            </div>


            {/* ADDRESS TYPE DROPDOWN */}
            <div className="mb-6">
              <div
                className="w-full p-3 border border-[#B1B1B1] bg-white text-gray-800 cursor-pointer flex items-center justify-between"
                onClick={() => setShowAddressTypeSheet(true)}
              >
                <span className={addressType ? "text-gray-800" : "text-gray-500"}>
                  {addressType || "Address type"}
                </span>
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* FLAT/HOUSE DETAILS */}
            <div className="mb-6">
              <LabeledInput
                label=""
                placeholder="Flat / House no / Floor / Building"
                value={flatDetails}
                onChange={(e) => setFlatDetails(e.target.value)}
              />
            </div>

            {/* NEARBY LANDMARK */}
            <div className="mb-8">
              <LabeledInput
                label=""
                placeholder="Nearby Landmark"
                value={landmark}
                onChange={(e) => setLandmark(e.target.value)}
              />
            </div>

            {/* SAVE ADDRESS BUTTON */}
            <div className="pb-[max(16px,env(safe-area-inset-bottom))]">
              <PrimaryButton
                label={loading ? "SAVING..." : (isEditMode ? "UPDATE ADDRESS" : "SAVE ADDRESS")}
                onClick={handleSaveAddress}
                disabled={loading}
                className="!w-full"
              />
            </div>
          </div>
        </main>
      </div>

      {/* ADDRESS TYPE BOTTOM SHEET */}
      <AddressTypeBottomSheet
        isOpen={showAddressTypeSheet}
        onClose={() => setShowAddressTypeSheet(false)}
        onSelect={handleAddressTypeSelect}
        selectedValue={addressType}
      />
    </div>
  );
};

export default AddAddress;
